import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PROPERTY_TYPES = ["House", "Flat", "Apartment", "Bungalow", "Cottage", "New Build", "Commercial", "Land"];
const PRICES = [0, 100000, 200000, 300000, 500000, 750000, 1000000, 1500000, 2000000];

export default function SearchBar({ variant = "hero" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [listing, setListing] = useState("any");
  const [type, setType] = useState("any");
  const [minPrice, setMinPrice] = useState("any");
  const [maxPrice, setMaxPrice] = useState("any");
  const [beds, setBeds] = useState("any");

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (type !== "any") params.set("property_type", type);
    if (minPrice !== "any") params.set("min_price", minPrice);
    if (maxPrice !== "any") params.set("max_price", maxPrice);
    if (beds !== "any") params.set("min_beds", beds);
    const base = listing === "sale" ? "/properties/for-sale" : listing === "let" ? "/properties/to-let" : "/properties";
    navigate(`${base}?${params.toString()}`);
  };

  const dark = variant === "hero";

  return (
    <form
      onSubmit={submit}
      className={`w-full rounded-2xl p-4 sm:p-5 grid grid-cols-2 lg:grid-cols-12 gap-3 ${dark ? "bg-white/95 backdrop-blur-xl shadow-2xl" : "bg-white border border-border shadow-sm"}`}
      data-testid="search-bar"
    >
      <div className="col-span-2 lg:col-span-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Location or postcode" className="h-11" data-testid="search-location" />
      </div>
      <div className="lg:col-span-2">
        <Select value={listing} onValueChange={setListing}>
          <SelectTrigger className="h-11" data-testid="search-listing"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Buy or Rent</SelectItem>
            <SelectItem value="sale">For Sale</SelectItem>
            <SelectItem value="let">To Let</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="lg:col-span-2">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-11" data-testid="search-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any type</SelectItem>
            {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="lg:col-span-2">
        <Select value={minPrice} onValueChange={setMinPrice}>
          <SelectTrigger className="h-11" data-testid="search-min-price"><SelectValue placeholder="Min" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Min price</SelectItem>
            {PRICES.map((p) => <SelectItem key={p} value={String(p)}>£{p.toLocaleString()}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="lg:col-span-2">
        <Select value={maxPrice} onValueChange={setMaxPrice}>
          <SelectTrigger className="h-11" data-testid="search-max-price"><SelectValue placeholder="Max" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Max price</SelectItem>
            {PRICES.filter((p) => p > 0).map((p) => <SelectItem key={p} value={String(p)}>£{p.toLocaleString()}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="lg:col-span-2">
        <Select value={beds} onValueChange={setBeds}>
          <SelectTrigger className="h-11" data-testid="search-beds"><SelectValue placeholder="Beds" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Min beds</SelectItem>
            {[1, 2, 3, 4, 5].map((b) => <SelectItem key={b} value={String(b)}>{b}+</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2 lg:col-span-10 lg:col-start-3">
        <Button type="submit" className="w-full h-11 rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2 font-semibold" data-testid="search-submit">
          <Search className="w-4 h-4" /> Search Properties
        </Button>
      </div>
    </form>
  );
}
